import React, { useState, useEffect, useRef } from 'react';
import { Search, MapPin, Truck, Phone, ChevronRight } from 'lucide-react';
import { carrierApi } from '../../services/api';
import { logger } from '../../utils/logger';
import { Alert } from '../shared/Alert';
import type { SearchResult } from '../../types/api';

interface CarrierSearchProps {
  onCarrierSelect: (dotNumber: number) => void;
  initialQuery?: string;
  className?: string;
  loading?: boolean;
}

export const CarrierSearch: React.FC<CarrierSearchProps> = ({
  onCarrierSelect,
  initialQuery = '',
  className = '',
  loading = false
}) => {
  // Search state
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchingType, setSearchingType] = useState<string>('');
  const [searchError, setSearchError] = useState('');
  // 'auto' uses the existing format-detection heuristics. Anything else
  // forces that exact search type, which is required for officer / address
  // since those have no obvious format to auto-detect.
  type SearchTypeOverride = 'auto' | 'dot' | 'mc' | 'mx' | 'ff' | 'name' | 'officer' | 'address' | 'vin';
  const [searchTypeOverride, setSearchTypeOverride] = useState<SearchTypeOverride>('auto');

  // Debounce timer for search
  const searchDebounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Handle search input change with debounce
  const handleSearchInputChange = (value: string) => {
    setSearchQuery(value);

    // Clear existing timer
    if (searchDebounceTimer.current) {
      clearTimeout(searchDebounceTimer.current);
    }

    // Set new timer - only search after 300ms of no typing
    searchDebounceTimer.current = setTimeout(() => {
      performSearch(value);
    }, 300);
  };

  // Actual search function (debounced)
  const performSearch = async (value: string) => {
    setSearchQuery(value);
    const query = value.trim();

    if (query.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    // Honour the explicit "Search by" dropdown if it isn't on Auto. This is
    // the only way to reach officer / address since they have no autodetect.
    if (searchTypeOverride !== 'auto') {
      setSearchingType(searchTypeOverride.toUpperCase());
      try {
        const cleaned = query.replace(/^(MC|MX|FF)-?/i, '');
        const results = await carrierApi.search(cleaned, searchTypeOverride);
        setSearchResults(results.slice(0, 10));
        setShowSearchResults(results.length > 0);
      } catch (err) {
        logger.error('Search error', err);
        setSearchResults([]);
        setShowSearchResults(false);
      }
      return;
    }

    // Auto-detect search type
    const isNumericDot = /^\d{1,7}$/.test(query);
    const isMC = /^MC-?\d+$/i.test(query);
    const isMX = /^MX-?\d+$/i.test(query);
    const isFF = /^FF-?\d+$/i.test(query);
    // VIN validation: 17 characters, no I, O, or Q
    const isVIN = /^[A-HJ-NPR-Z0-9]{17}$/i.test(query);

    // Direct DOT number - no need to search, just validate
    if (isNumericDot) {
      setSearchResults([]);
      setShowSearchResults(false);
      setSearchingType('DOT Number');
      return;
    }

    // For VIN search, use special endpoint
    if (isVIN) {
      setSearchingType('VIN');
      try {
        const vinData = await carrierApi.getCarriersByVIN(query);
        if (vinData && vinData.carriers && vinData.carriers.length > 0) {
          // Transform VIN carriers data to match search results format
          const vinResults: SearchResult[] = vinData.carriers.map((carrier) => ({
            dotNumber: carrier.dotNumber,
            legalName: carrier.legalName || 'Unknown',
            dbaName: null,
            phyCity: null,
            phyState: null,
            statusCode: '',
            safetyRating: null,
            totalPowerUnits: 0,
            matchType: 'name' as const,
            relevanceScore: carrier.inspectionCount || 0
          }));
          setSearchResults(vinResults.slice(0, 10));
          setShowSearchResults(vinResults.length > 0);
        } else {
          setSearchResults([]);
          setShowSearchResults(false);
        }
      } catch (err) {
        logger.error('VIN search error', err);
        setSearchResults([]);
        setShowSearchResults(false);
      }
      return;
    }

    // For MC/MX/FF/Name - show search results
    const searchType = isMC ? 'mc' : isMX ? 'mx' : isFF ? 'ff' : 'name';
    const searchQueryClean = query.replace(/^(MC|MX|FF)-?/i, '');

    setSearchingType(searchType.toUpperCase());

    try {
      const results = await carrierApi.search(searchQueryClean, searchType);
      setSearchResults(results.slice(0, 10)); // Show max 10 results
      setShowSearchResults(results.length > 0);
    } catch (err) {
      logger.error('Search error', err);
      setSearchResults([]);
      setShowSearchResults(false);
    }
  };

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }
    };
  }, []);

  // Handle selecting a carrier from search results
  const handleSelectCarrier = (selectedDot: number) => {
    // Validate DOT number is a positive integer
    if (!selectedDot || selectedDot <= 0 || !Number.isInteger(selectedDot)) {
      logger.error('handleSelectCarrier called with invalid DOT number', { selectedDot });
      return;
    }
    console.log('[CarrierSearch] handleSelectCarrier called with DOT:', selectedDot);
    setSearchQuery(selectedDot.toString());
    setShowSearchResults(false);
    setSearchResults([]);
    console.log('[CarrierSearch] Calling onCarrierSelect with DOT:', selectedDot);
    onCarrierSelect(selectedDot);
  };

  // Handle form submit - execute search and show results list
  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const query = searchQuery.trim();

    console.log('[CarrierSearch] ========================================');
    console.log('[CarrierSearch] handleSearch called with query:', query);
    console.log('[CarrierSearch] Event prevented:', e.defaultPrevented);

    if (!query) {
      console.log('[CarrierSearch] Empty query, showing error');
      setSearchError('Please enter a search query');
      return;
    }

    // Clear previous search state
    setSearchError('');
    console.log('[CarrierSearch] Starting search for:', query);

    try {
      // Honour the explicit "Search by" dropdown when it isn't Auto. We send
      // straight to the typed endpoint and skip the auto-detection branches.
      if (searchTypeOverride !== 'auto') {
        const cleaned = query.replace(/^(MC|MX|FF)-?/i, '');
        const results = await carrierApi.search(cleaned, searchTypeOverride);
        setSearchingType(searchTypeOverride.toUpperCase());
        if (results.length > 0) {
          setSearchResults(results);
          setShowSearchResults(true);
        } else {
          setSearchError(`No carrier found via ${searchTypeOverride} search for "${query}"`);
          setShowSearchResults(false);
        }
        return;
      }

      // Auto-detect search type
      const isNumericDot = /^\d{1,7}$/.test(query);
      const isMC = /^MC-?\d+$/i.test(query);
      const isMX = /^MX-?\d+$/i.test(query);
      const isFF = /^FF-?\d+$/i.test(query);
      const isVIN = /^[A-HJ-NPR-Z0-9]{17}$/i.test(query);

      // If numeric, could be DOT or MC - search both
      if (isNumericDot) {
        const number = parseInt(query);
        if (number && number > 0) {
          // Search for both DOT and MC matches
          try {
            const [dotSearch, mcSearch] = await Promise.all([
              carrierApi.search(query, 'dot'),
              carrierApi.search(query, 'mc')
            ]);

            console.log('[CarrierSearch] Search results:', { dotSearch, mcSearch });

            // Deduplicate results by DOT number
            const combinedResults = [...(dotSearch || []), ...(mcSearch || [])];
            const seenDots = new Set<number>();
            const allResults = combinedResults.filter(result => {
              if (seenDots.has(result.dotNumber)) {
                return false;
              }
              seenDots.add(result.dotNumber);
              return true;
            });

            if (allResults.length === 0) {
              setSearchError(`No carrier found with DOT or MC ${query}`);
              return;
            } else {
              // Show results list even if only 1 match
              setSearchResults(allResults);
              setShowSearchResults(true);
              setSearchingType('DOT/MC');
            }
            return;
          } catch (err) {
            logger.error('Ambiguous search error:', err);
            // Fallback to just loading as DOT
            setShowSearchResults(false);
            handleSelectCarrier(number);
            return;
          }
        }
        return;
      }

      // For VIN search, use special endpoint
      if (isVIN) {
        logger.debug('Executing VIN search', { vin: query });
        const vinData = await carrierApi.getCarriersByVIN(query);

        if (vinData && vinData.carriers && vinData.carriers.length > 0) {
          // Transform VIN carriers data to match search results format
          const vinResults: SearchResult[] = vinData.carriers.map((carrier) => ({
            dotNumber: carrier.dotNumber,
            legalName: carrier.legalName || 'Unknown',
            dbaName: null,
            phyCity: null,
            phyState: null,
            statusCode: '',
            safetyRating: null,
            totalPowerUnits: 0,
            matchType: 'name' as const,
            relevanceScore: carrier.inspectionCount || 0
          }));

          // Show VIN results list (even for single result)
          setSearchResults(vinResults);
          setSearchingType('VIN');
          setShowSearchResults(true);
        } else {
          setSearchError(`No carriers found for VIN "${query}"`);
          setShowSearchResults(false);
        }
        return;
      }

      // For MC/MX/FF/Name - search and show results list
      const searchType = isMC ? 'mc' : isMX ? 'mx' : isFF ? 'ff' : 'name';
      const searchQueryClean = query.replace(/^(MC|MX|FF)-?/i, '');

      logger.debug('Executing search:', { query: searchQueryClean, type: searchType });
      const results = await carrierApi.search(searchQueryClean, searchType);

      // Also search DBA if searching by name
      if (searchType === 'name') {
        const dbaResults = await carrierApi.search(searchQueryClean, 'dba');
        // Merge results, avoiding duplicates
        const combined = [...results];
        dbaResults.forEach(dbaResult => {
          if (!combined.find(r => r.dotNumber === dbaResult.dotNumber)) {
            combined.push(dbaResult);
          }
        });
        setSearchResults(combined);
      } else {
        setSearchResults(results);
      }

      setSearchingType(searchType.toUpperCase());

      // Show results list (even for single result)
      if (results.length > 0) {
        setSearchResults(results);
        setShowSearchResults(true);
      } else {
        setSearchError(`No carrier found matching "${query}"`);
        setShowSearchResults(false);
      }
      // Results list will be displayed below (showSearchResults=true)

    } catch (error: unknown) {
      logger.error('Search error:', error);
      setSearchError(error instanceof Error ? error.message : 'Search failed. Please try again.');
      setShowSearchResults(false);
    }
  };

  return (
    <div className={className}>
      {/* Search Input Form */}
      <div className="container-spacious">
        <div className="card-modern fade-in-up" style={{ animationDelay: '0.1s' }}>
          <div style={{ marginBottom: 'var(--space-lg)' }}>
            <h2 className="heading-2" style={{ marginBottom: 'var(--space-xs)' }}>Carrier Search & Analytics</h2>
            <p className="text-muted">Enter a DOT number to access comprehensive carrier intelligence and safety data</p>
          </div>

          <form onSubmit={handleSearch} style={{ display: 'flex', gap: 'var(--space-md)', flexWrap: 'wrap' }}>
            <div style={{ flex: '0 0 auto' }}>
              <label htmlFor="search-by" className="text-muted" style={{ display: 'block', fontSize: '0.75rem', marginBottom: 'var(--space-xs)' }}>Search by</label>
              <select
                id="search-by"
                value={searchTypeOverride}
                onChange={(e) => setSearchTypeOverride(e.target.value as SearchTypeOverride)}
                style={{
                  padding: 'var(--space-md)',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: 'var(--radius-lg)',
                  fontSize: '1rem',
                  background: 'white',
                  cursor: 'pointer',
                  minWidth: '160px'
                }}
              >
                <option value="auto">Auto-detect</option>
                <option value="dot">DOT #</option>
                <option value="mc">MC #</option>
                <option value="name">Company name</option>
                <option value="officer">Officer name</option>
                <option value="address">Address (street/city/state/ZIP)</option>
                <option value="vin">VIN</option>
              </select>
            </div>
            <div style={{ flex: '1', minWidth: '300px' }}>
              <label htmlFor="search-input" className="text-muted" style={{ display: 'block', fontSize: '0.75rem', marginBottom: 'var(--space-xs)' }}>Search term</label>
              <input
                id="search-input"
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchInputChange(e.target.value)}
                placeholder={
                  searchTypeOverride === 'officer' ? 'Officer name (e.g. JOHN DOE)' :
                  searchTypeOverride === 'address' ? 'Street, city, state, or ZIP' :
                  searchTypeOverride === 'vin'     ? '17-character VIN' :
                  searchTypeOverride === 'dot'     ? 'DOT number' :
                  searchTypeOverride === 'mc'      ? 'MC number' :
                  searchTypeOverride === 'name'    ? 'Company name (legal or DBA)' :
                  'Search by DOT, MC, MX, FF, VIN, or Company Name'
                }
                style={{
                  width: '100%',
                  padding: 'var(--space-md)',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: 'var(--radius-lg)',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'border-color 0.2s, box-shadow 0.2s'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--color-primary)';
                  e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--color-gray-300)';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <p className="text-muted" style={{ marginTop: 'var(--space-sm)', fontSize: '0.75rem' }}>
                Try: DOT 123456, MC 12345, or company name like "ACME TRUCKING"
              </p>
            </div>
            <button
              type="submit"
              disabled={loading}
              className={`btn-primary ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-sm)' }}
            >
              {loading ? (
                <>Loading...</>
              ) : (
                <>
                  <Search size={18} />
                  Search
                </>
              )}
            </button>
          </form>

          {searchError && (
            <div style={{ marginTop: 'var(--space-lg)' }}>
              <Alert type="danger" message={searchError} onClose={() => setSearchError('')} />
            </div>
          )}
        </div>
      </div>

      {/* Search Results List - Shows when search returns matches */}
      {showSearchResults && searchResults.length > 0 && !loading && (
        <div className="container-spacious">
          <div className="card-modern fade-in-up" style={{ animationDelay: '0.2s' }}>
            <div style={{ marginBottom: 'var(--space-lg)' }}>
              <h3 className="heading-3" style={{ marginBottom: 'var(--space-xs)' }}>
                {searchingType === 'VIN'
                  ? `Found ${searchResults.length} Carrier${searchResults.length === 1 ? '' : 's'} Using This VIN`
                  : `Found ${searchResults.length} ${searchingType} ${searchResults.length === 1 ? 'Match' : 'Matches'}`
                }
              </h3>
              <p className="text-muted">
                {searchingType === 'VIN'
                  ? 'These carriers have operated vehicles with this VIN. Click to view their safety profile.'
                  : 'Click on any carrier below to view their full profile and safety data'
                }
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
              {searchResults.map((result, index) => (
                <div
                  key={`${result.dotNumber}-${index}`}
                  onClick={() => handleSelectCarrier(result.dotNumber)}
                  className="card-modern-compact"
                  style={{
                    padding: 'var(--space-md)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--color-gray-50)';
                    e.currentTarget.style.borderColor = 'var(--color-gray-300)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.borderColor = 'var(--color-gray-200)';
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 'var(--space-xl)' }}>
                    {/* Left: Carrier Info */}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--color-gray-900)', marginBottom: 'var(--space-sm)' }}>
                        {result.legalName || result.dbaName || 'Unknown Carrier'}
                      </div>
                      {result.dbaName && result.legalName !== result.dbaName && (
                        <div style={{ fontSize: '0.9375rem', color: 'var(--color-gray-700)', marginBottom: 'var(--space-sm)' }}>
                          <strong>DBA:</strong> {result.dbaName}
                        </div>
                      )}
                      <div style={{ fontSize: '0.9375rem', color: 'var(--color-gray-600)', display: 'flex', gap: 'var(--space-lg)', flexWrap: 'wrap', marginTop: 'var(--space-md)' }}>
                        {(result.phyCity || result.phyState) && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
                            <MapPin size={16} />
                            {result.phyCity}, {result.phyState}
                          </span>
                        )}
                        {result.total_power_units > 0 && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
                            <Truck size={16} />
                            {result.total_power_units} Power Units
                          </span>
                        )}
                      </div>
                      {result.safety_rating && (
                        <div style={{ marginTop: 'var(--space-md)' }}>
                          <span className={`badge-modern ${
                            result.safety_rating === 'S' ? 'badge-success' :
                            result.safety_rating === 'U' ? 'badge-danger' :
                            'badge-warning'
                          }`}>
                            Safety: {result.safety_rating === 'S' ? 'Satisfactory' : result.safety_rating === 'U' ? 'Unsatisfactory' : 'Conditional'}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Right: Badges */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)', alignItems: 'flex-end', minWidth: '140px' }}>
                      <div className="badge-modern badge-primary" style={{ fontSize: '0.9375rem', fontWeight: 600, padding: '0.5rem 1rem' }}>
                        DOT {result.dotNumber}
                      </div>
                    </div>
                  </div>

                  {/* Click indicator */}
                  <div style={{
                    marginTop: 'var(--space-lg)',
                    paddingTop: 'var(--space-md)',
                    borderTop: '1px solid var(--color-gray-100)',
                    textAlign: 'center',
                    fontSize: '0.875rem',
                    color: 'var(--color-primary)',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 'var(--space-xs)'
                  }}>
                    Click to view complete carrier profile
                    <ChevronRight size={16} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
